import {getAge} from "@khan/auth-shared";

import type {ProfileModelType} from "./profile-model.ts";

/**
 * Determines if the birthday provided is older than 18 years.
 * The day is irrelevant for this calculation since our business rules
 * dictate that the last of the month is used. (We don't collect day of birth,
 * only month and year)
 *
 * NOTE: This function is for logged-out users. For logged-in users, it's more
 * convenient to use isConsideredOver18() or isDefinitelyOver18()
 * instead.
 *
 * @param birthday - a users birthday
 * @returns True if the birthday is over 18 years, false otherwise
 */
export function isDateOver18(birthday: Date | string): boolean {
    return getAge(birthday) >= 18;
}

/**
 * Helper function that takes a profile and returns a boolean indicating
 * whether the user is probably over 18.
 * - Teachers and parents are considered to probably be 18 or older.
 * - Learners without a birthdate are considered to be under 18.
 * - If you need to be more strict, and birthday is required, use
 *   `isDefinitelyOver18`.
 *
 * NOTE: because we calculate based on birthMonthYear, the learners birthday
 * is considered to be the last day of the month.
 */
export function isConsideredOver18(profile: ProfileModelType): boolean {
    if (!profile) {
        return false;
    }

    // These types of users don't have birthdates, but are considered
    // over 18.
    if (profile?.get("isParent") || profile?.get("isTeacher")) {
        return true;
    }

    return isDefinitelyOver18(profile);
}

/**
 * Helper function that takes a profile and returns a boolean indicating
 * whether the user is definitely over 18.
 * - Teachers, Parents, and Learners, without a birthdate are considered to be
 *   under 18.
 * - If you want to consider all Teachers and Parents as over 18, use
 *   `isConsideredOver18`.
 *
 * NOTE: because we calculate based on birthMonthYear, the learners birthday
 * is considered to be the last day of the month.
 */
export function isDefinitelyOver18(profile: ProfileModelType): boolean {
    if (!profile) {
        return false;
    }

    const birthMonthYear = profile.get("birthMonthYear");
    // If we don't have a birthdate, the user is considered not over 18.
    if (!birthMonthYear) {
        return false;
    }

    return isDateOver18(birthMonthYear);
}
